import { SIGNUP_URL, STUDIO_SIGNUP_URL } from "./app";
import { SALES_EMAIL_HREF } from "./contact";
import { HOME_PATH } from "./nav";
import { DEDICATED_SUPPORT_LABEL, EXPORT_TIER, FREE_TIER, MACHINE_EXPORT_LABEL, SEAT_TIER } from "./packaging";
import { SEAT_MONTHLY_USD } from "./pricing";
import { TRADEMARK, type Copy } from "./types";

export type PlanFeature = {
  readonly label: string;
  readonly included: boolean;
};

export type PlanPrice =
  | { readonly kind: "seat"; readonly monthlyUsd: number }
  | { readonly kind: "custom"; readonly label: string; readonly billed: string };

export type Plan = {
  readonly id: string;
  readonly name: Copy;
  readonly price: PlanPrice;
  readonly note: string;
  readonly highlighted?: boolean;
  readonly features: readonly PlanFeature[];
  readonly cta: {
    readonly label: string;
    readonly href: string;
    readonly variant: "brand" | "ghost" | "outline";
    readonly external?: boolean;
  };
};

export const BILLING_LEGEND = "Billing period";

export const BILLING_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "annual", label: "Annual" },
] as const;

export const PLANS: readonly Plan[] = [
  {
    id: "trial",
    name: ["LOOP", TRADEMARK, ` ${FREE_TIER}`],
    price: {
      kind: "custom",
      label: "Free",
      billed: "Free to start. Move up whenever you're ready.",
    },
    note: "The whole design flow, on your own geometry, at no cost.",
    highlighted: true,
    features: [
      { label: "VARIANT3D project templates", included: true },
      { label: "Knitmesh and schematic visualization", included: true },
      { label: "Project versioning", included: true },
      { label: "Drawing tool and design on V3D default knit structures", included: true },
      { label: "Share a rendered Spin preview of any design as a public link", included: true },
      { label: "Import custom 3D assets — OBJ, STL and .3dm", included: true },
      { label: "Unlimited projects", included: true },
      { label: "Custom knit structures, written in code", included: true },
      { label: MACHINE_EXPORT_LABEL, included: false },
    ],
    cta: {
      label: "Try it free",
      href: SIGNUP_URL,
      variant: "brand",
    },
  },
  {
    id: "studio",
    name: ["LOOP", TRADEMARK, ` ${SEAT_TIER} Seat`],
    price: { kind: "seat", monthlyUsd: SEAT_MONTHLY_USD },
    note: "Machine file export for your own geometry, on one seat.",
    features: [
      { label: `Everything in ${FREE_TIER}`, included: true },
      { label: "Machine file export, to run on your own or partnering machines", included: true },
      { label: "Access to V3D Production system (Pay to Produce)", included: true },
      { label: DEDICATED_SUPPORT_LABEL, included: false },
    ],
    cta: {
      label: "Start designing",
      href: STUDIO_SIGNUP_URL,
      variant: "outline",
    },
  },
  {
    id: "enterprise",
    name: ["LOOP", TRADEMARK, ` ${EXPORT_TIER}`],
    price: {
      kind: "custom",
      label: "Custom",
      billed: "Annual agreements, with volume terms on seat count.",
    },
    note: "For teams running their own production.",
    features: [
      { label: `Everything in ${SEAT_TIER}, across your teams`, included: true },
      { label: "Every new LOOP feature as it ships, including betas", included: true },
      { label: "A dedicated instance for your cloud service and project database", included: true },
      { label: "Share projects across your own organization", included: true },
      { label: "BMP export for off-ramp development", included: true },
      { label: "Knitdown licensed for commercial resale", included: true },
      { label: "Custom IAM process and support", included: true },
      { label: "Machine integration support", included: true },
      { label: "Dedicated customer support from the VARIANT3D team", included: true },
    ],
    cta: {
      label: "Contact sales",
      href: SALES_EMAIL_HREF,
      variant: "outline",
    },
  },
];

export const PRICING_HERO = {
  eyebrow: "Pricing",
  title: "The platform to knit everything",
  lede: "Software, embedded knit expertise, and production access in one platform.",
} as const;

export const BACK_LINK = {
  href: HOME_PATH,
  title: ["Back to LOOP", TRADEMARK] as Copy,
  note: "How it works, what it's for, and what the old way costs",
} as const;
